import React from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import blogData from '../data/blogs.json';

export default function Blog() {
  const { blogId } = useParams();
  const blog = blogData.blogs.find(b => b.id === blogId);
  const siteUrl = 'https://baggainternational.com';

  if (!blog) {
    return (
      <div className="container mx-auto px-4 py-8">
        <h2 className="text-3xl font-bold text-center mb-8">Blog post not found</h2>
        <div className="text-center">
          <Link to="/blog" className="text-green-600 hover:underline">← Back to Blog</Link>
        </div>
      </div>
    );
  }

  const canonicalUrl = `${siteUrl}/blog/${blogId}`;
  const metaDescription = blog.excerpt;
  const ogImage = blog.image;

  return (
    <React.Fragment>
      <HelmetProvider>
        <Helmet>
          <title>{`${blog.title} | Bagga International`}</title>
          <meta name="description" content={metaDescription} />
          <link rel="canonical" href={canonicalUrl} />
          {/* Open Graph tags */}
          <meta property="og:title" content={blog.title} />
          <meta property="og:description" content={metaDescription} />
          <meta property="og:image" content={ogImage} />
          <meta property="og:url" content={canonicalUrl} />
          <meta property="og:type" content="article" />
          {/* Twitter Card tags */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={blog.title} />
          <meta name="twitter:description" content={metaDescription} />
          <meta name="twitter:image" content={ogImage} />
          {/* Article structured data */}
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org/",
              "@type": "Article",
              "headline": blog.title,
              "image": [ogImage],
              "description": metaDescription,
              "author": {
                "@type": "Organization",
                "name": "Bagga International"
              },
              "publisher": {
                "@type": "Organization",
                "name": "Bagga International",
                "logo": {
                  "@type": "ImageObject",
                  "url": `${siteUrl}/images/site-default-og.jpg`
                }
              },
              "datePublished": blog.publishDate,
              "dateModified": blog.publishDate
            })}
          </script>
        </Helmet>
      </HelmetProvider>

      {/* Blog Post Content */}
      <section className="pt-20 pb-16 bg-white">
        <div className="container px-4 mx-auto">

          {/* Main Content Container */}
          <div className="max-w-4xl mx-auto">
            {/* Blog Header */}
            <div className="text-center mb-12">
              <div className="mb-6">
                <span className="inline-block py-2 px-4 text-sm leading-5 text-green-500 bg-green-100 font-semibold uppercase rounded-full shadow-sm">
                  {blog.category}
                </span>
              </div>
              <h1 className="mb-6 text-4xl md:text-5xl lg:text-6xl leading-tight text-darkCoolGray-900 font-bold tracking-tighter">
                {blog.title}
              </h1>
              <p className="mb-8 text-xl md:text-2xl text-coolGray-600 font-medium leading-relaxed max-w-3xl mx-auto">
                {blog.excerpt}
              </p>
              
              {/* Enhanced Blog Meta */}
              <div className="flex flex-wrap items-center justify-center gap-8 text-sm text-coolGray-500 border-b border-coolGray-200 pb-8">
                {/* Author */}
                <div className="flex items-center group">
                  <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-green-400 to-green-600 rounded-full mr-3 shadow-md group-hover:shadow-lg transition-all duration-200">
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                  <div className="text-left">
                    <div className="text-xs text-coolGray-400 uppercase tracking-wide font-semibold">Author</div>
                    <div className="font-semibold text-coolGray-700">{blog.author}</div>
                  </div>
                </div>

                {/* Publication Date */}
                <div className="flex items-center group">
                  <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full mr-3 shadow-md group-hover:shadow-lg transition-all duration-200">
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                  <div className="text-left">
                    <div className="text-xs text-coolGray-400 uppercase tracking-wide font-semibold">Published</div>
                    <div className="font-semibold text-coolGray-700">
                      {new Date(blog.publishDate).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </div>
                  </div>
                </div>

                {/* Read Time */}
                <div className="flex items-center group">
                  <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full mr-3 shadow-md group-hover:shadow-lg transition-all duration-200">
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                  <div className="text-left">
                    <div className="text-xs text-coolGray-400 uppercase tracking-wide font-semibold">Read Time</div>
                    <div className="font-semibold text-coolGray-700">{blog.readTime}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Blog Image */}
            <div className="mb-16">
              <div className="relative overflow-hidden rounded-2xl shadow-2xl">
                <img 
                  src={blog.image} 
                  alt={blog.title}
                  className="w-full h-80 md:h-96 lg:h-[500px] object-cover transform hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
              </div>
            </div>

            {/* Blog Content */}
            <div className="prose prose-lg max-w-none">
              <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
                {blog.content.map((paragraph, index) => (
                  <p key={index} className="mb-8 text-lg md:text-xl leading-relaxed text-coolGray-700 font-normal">
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>

            {/* Tags Section */}
            <div className="mt-16 pt-8 border-t border-coolGray-200">
              <div className="bg-coolGray-50 rounded-2xl p-8">
                <h3 className="text-xl font-bold text-darkCoolGray-900 mb-6 text-center">Related Topics</h3>
                <div className="flex flex-wrap justify-center gap-3">
                  {blog.tags.map((tag, index) => (
                    <span 
                      key={index}
                      className="inline-block py-2 px-4 text-sm leading-5 text-green-600 bg-green-100 font-semibold rounded-full border border-green-200 hover:bg-green-200 transition-colors cursor-pointer"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </React.Fragment>
  );
}

